import { useCallback, useRef } from 'react';
import api from '../utils/api';

/**
 * useOnboardingAnalytics - Hook for tracking onboarding analytics
 *
 * Tracks step views, time spent, completions, and user interactions.
 * Data is batched and sent to the backend periodically.
 */
export function useOnboardingAnalytics(userId) {
  const eventsBuffer = useRef([]);
  const stepStartTime = useRef(null);
  const flushTimeout = useRef(null);

  // Flush events to backend
  const flushEvents = useCallback(async () => {
    if (eventsBuffer.current.length === 0) return;

    const events = [...eventsBuffer.current];
    eventsBuffer.current = [];

    try {
      await api.post('/users/me/onboarding-analytics', { events });
    } catch (err) {
      // Silently fail - analytics is non-critical, localStorage is fallback
      console.warn('Could not sync onboarding analytics:', err.message);
    }
  }, []);

  // Schedule flush
  const scheduleFlush = useCallback(() => {
    if (flushTimeout.current) {
      clearTimeout(flushTimeout.current);
    }
    flushTimeout.current = setTimeout(flushEvents, 5000);
  }, [flushEvents]);

  // Track event
  const trackEvent = useCallback((eventType, data = {}) => {
    const event = {
      type: eventType,
      timestamp: new Date().toISOString(),
      userId,
      ...data,
    };

    eventsBuffer.current.push(event);
    scheduleFlush();

    // Also store in localStorage for redundancy
    try {
      const stored = JSON.parse(localStorage.getItem('onboarding_analytics') || '[]');
      stored.push(event);
      // Keep only last 50 events
      localStorage.setItem('onboarding_analytics', JSON.stringify(stored.slice(-50)));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [userId, scheduleFlush]);

  // Track step view
  const trackStepView = useCallback((stepId, stepIndex) => {
    // End timing for previous step
    if (stepStartTime.current) {
      const duration = Date.now() - stepStartTime.current.time;
      trackEvent('step_duration', {
        stepId: stepStartTime.current.stepId,
        duration,
      });
    }

    // Start timing for new step
    stepStartTime.current = { stepId, time: Date.now() };

    trackEvent('step_view', { stepId, stepIndex });
  }, [trackEvent]);

  // Track step action (e.g., clicking a link)
  const trackStepAction = useCallback((stepId, actionType, actionData = {}) => {
    trackEvent('step_action', { stepId, actionType, ...actionData });
  }, [trackEvent]);

  // Track wizard completion
  const trackComplete = useCallback((totalSteps, skippedSteps = []) => {
    const totalTime = stepStartTime.current
      ? Date.now() - stepStartTime.current.time
      : 0;

    trackEvent('wizard_complete', {
      totalSteps,
      skippedSteps,
      totalTime,
    });

    // Flush immediately on complete
    flushEvents();
  }, [trackEvent, flushEvents]);

  // Track wizard skip
  const trackSkip = useCallback((currentStep, totalSteps) => {
    trackEvent('wizard_skip', {
      skippedAtStep: currentStep,
      totalSteps,
    });

    // Flush immediately on skip
    flushEvents();
  }, [trackEvent, flushEvents]);

  // Track tip interaction
  const trackTipInteraction = useCallback((tipId, action) => {
    trackEvent('tip_interaction', { tipId, action });
  }, [trackEvent]);

  // Track checklist item completion
  const trackChecklistItem = useCallback((itemId, completed) => {
    trackEvent('checklist_item', { itemId, completed });
  }, [trackEvent]);

  // Track interactive feature usage
  const trackInteractiveFeature = useCallback((featureId, action) => {
    trackEvent('interactive_feature', { featureId, action });
  }, [trackEvent]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    if (flushTimeout.current) {
      clearTimeout(flushTimeout.current);
    }
    flushEvents();
  }, [flushEvents]);

  return {
    trackStepView,
    trackStepAction,
    trackComplete,
    trackSkip,
    trackTipInteraction,
    trackChecklistItem,
    trackInteractiveFeature,
    cleanup,
  };
}

export default useOnboardingAnalytics;
